/**
 * Clean Command
 * Clean up backups, cache, and temporary files
 * 
 * Renamed from 'clear-backups' to 'clean' for broader scope
 */
import { Command } from 'commander';
import { confirm, select } from '@inquirer/prompts';
import { ComponentsService } from '../services/components.service.js';
import { promises as fs } from 'fs';
import { join } from 'path';
import { existsSync } from 'fs';
import { selectTheme } from '../utils/prompt-theme.js';

interface CleanTarget {
    name: string;
    description: string;
    path: string;
    check: () => Promise<{ exists: boolean; count?: number; size?: string }>;
    clean: () => Promise<void>;
}

/**
 * Create clean command
 */
export function createCleanCommand(): Command {
    return new Command('clean')
        .description('Clean up backups, cache, IDE configs, and .jai1 directory')
        .option('-y, --yes', 'Skip confirmation')
        .option('--backups', 'Clean only backup files')
        .option('--jai1', 'Clean only .jai1/ directory')
        .option('--ide', 'Clean only IDE directories (.cursor, .windsurf, .agent, .claude, .opencode, .codex)')
        .option('--all', 'Clean all (backups + .jai1 + IDE dirs)')
        .action(async (options: { yes?: boolean; backups?: boolean; jai1?: boolean; ide?: boolean; all?: boolean }) => {
            await handleClean(options);
        });
}

async function handleClean(options: { yes?: boolean; backups?: boolean; jai1?: boolean; ide?: boolean; all?: boolean }) {
    const cwd = process.cwd();
    const service = new ComponentsService();

    // Define clean targets
    const targets: CleanTarget[] = [
        {
            name: 'Backups',
            description: 'Component backup files (.jai1_backup/)',
            path: join(cwd, '.jai1_backup'),
            check: async () => {
                const backups = await service.listBackups(cwd);
                return { exists: backups.length > 0, count: backups.length };
            },
            clean: async () => {
                await service.clearBackups(cwd);
            }
        },
        {
            name: 'Jai1 Config',
            description: 'Jai1 framework config (.jai1/)',
            path: join(cwd, '.jai1'),
            check: async () => {
                const exists = existsSync(join(cwd, '.jai1'));
                return { exists };
            },
            clean: async () => {
                await fs.rm(join(cwd, '.jai1'), { recursive: true, force: true });
            }
        },
    ];

    // Dynamically detect IDE directories that exist in the project
    const ideDirectories = [
        { name: 'Cursor', dir: '.cursor' },
        { name: 'Windsurf', dir: '.windsurf' },
        { name: 'Antigravity', dir: '.agent' },
        { name: 'Claude Code', dir: '.claude' },
        { name: 'OpenCode', dir: '.opencode' },
        { name: 'Codex', dir: '.codex' },
    ];

    for (const ide of ideDirectories) {
        const idePath = join(cwd, ide.dir);
        if (existsSync(idePath)) {
            targets.push({
                name: `IDE: ${ide.name}`,
                description: `${ide.name} IDE config (${ide.dir}/)`,
                path: idePath,
                check: async () => ({ exists: true }),
                clean: async () => {
                    await fs.rm(idePath, { recursive: true, force: true });
                }
            });
        }
    }

    console.log('🧹 Clean up CLI client files\n');

    // Check what's available to clean
    const availableTargets: Array<{ target: CleanTarget; info: { exists: boolean; count?: number } }> = [];

    for (const target of targets) {
        const info = await target.check();
        if (info.exists) {
            availableTargets.push({ target, info });
        }
    }

    if (availableTargets.length === 0) {
        console.log('✨ Nothing to clean up!');
        return;
    }

    // If specific option provided
    if (options.backups) {
        const backupTarget = targets.find(t => t.name === 'Backups');
        if (backupTarget) {
            await cleanTarget(backupTarget, options.yes);
        }
        return;
    }

    if (options.jai1) {
        const jai1Target = availableTargets.find(({ target }) => target.name === 'Jai1 Config');
        if (jai1Target) {
            await cleanTarget(jai1Target.target, options.yes);
        } else {
            console.log('✨ No .jai1/ directory found.');
        }
        return;
    }

    if (options.ide) {
        const ideTargets = availableTargets.filter(({ target }) => target.name.startsWith('IDE:'));
        if (ideTargets.length === 0) {
            console.log('✨ No IDE directories found.');
            return;
        }
        for (const { target } of ideTargets) {
            await cleanTarget(target, options.yes);
        }
        return;
    }

    if (options.all) {
        for (const { target } of availableTargets) {
            await cleanTarget(target, options.yes);
        }
        return;
    }

    // Interactive mode
    console.log('Found the following items:\n');
    for (const { target, info } of availableTargets) {
        const countStr = info.count ? ` (${info.count} items)` : '';
        console.log(`  📁 ${target.name}${countStr}`);
        console.log(`     ${target.description}`);
        console.log(`     Path: ${target.path}\n`);
    }

    const action = await select({
        message: 'What do you want to clean?',
        choices: [
            ...availableTargets.map(({ target, info }) => ({
                name: `${target.name}${info.count ? ` (${info.count} items)` : ''}`,
                value: target.name
            })),
            { name: '🧹 Clean all', value: 'all' },
            { name: '❌ Cancel', value: 'cancel' }
        ],
        theme: selectTheme,
    });

    if (action === 'cancel') {
        console.log('\n❌ Operation cancelled.');
        return;
    }

    if (action === 'all') {
        for (const { target } of availableTargets) {
            await cleanTarget(target, options.yes);
        }
    } else {
        const target = targets.find(t => t.name === action);
        if (target) {
            await cleanTarget(target, options.yes);
        }
    }
}

async function cleanTarget(target: CleanTarget, skipConfirm?: boolean): Promise<void> {
    const info = await target.check();

    if (!info.exists) {
        console.log(`✨ ${target.name}: Nothing to clean.`);
        return;
    }

    const countStr = info.count ? ` (${info.count} items)` : '';

    if (!skipConfirm) {
        const confirmed = await confirm({
            message: `Delete ${target.name}${countStr}?`,
            default: false
        });

        if (!confirmed) {
            console.log(`⏭️  Skipped ${target.name}`);
            return;
        }
    }

    try {
        await target.clean();
        console.log(`✅ Cleaned ${target.name}${countStr}`);
    } catch (error) {
        console.error(`❌ Failed to clean ${target.name}: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
}
